import { Button, DataTableController } from 'components/common'
import { ReportIcon } from 'components/icons'
import {
  columns,
  FILTER_FIELD_ORDERS,
  OrderFilterForm,
  OrdersTable,
  OrderReportsModal,
} from 'components/orderManagement'
import { ImportColumn } from 'components/common/importModal'
import ActiveFilterChip from 'components/common/activeFilterChip'
import { usePageAccess, useQuery } from 'hooks'
import { useEffect, useState } from 'react'
import { getImportTemplate, downloadImportSample } from 'utils/api/importApi'
import { Helmet } from 'react-helmet'
import toast from 'react-hot-toast'
import {
  downloadOrders,
  importOrdersApi,
  triggerLastmileSync,
  triggerOrderSync,
  triggerReconciliationSync,
  triggerRmaSync,
} from 'utils/api/orderApi'
import { filterEmptyValues } from 'utils/helpers'
import { handleError } from 'utils/helpers/handleError'

const CRON_TYPE = {
  ORDER: 'Order Sync',
  RECONCILIATION: 'Reconciliation Sync',
  RMA: 'Rma Sync',
  LASTMILE: 'Lastmile Sync',
}

const OrderListPage = () => {
  const { isEditor } = usePageAccess()
  const [showFilter, setShowFilter] = useState(false)
  const [selectedColumns, setSelectedColumns] = useState<string[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [triggerLoading, setTriggerLoading] = useState<string>()
  const [importColumns, setImportColumns] = useState<ImportColumn[]>([])
  const [showReportsModal, setShowReportsModal] = useState(false)

  const { getQuery } = useQuery()

  useEffect(() => {
    getImportTemplate('order')
      .then((res: { data: { columns: ImportColumn[] } }) =>
        setImportColumns(res.data.columns),
      )
      .catch(() => {})
  }, [])

  const handleDownloadSample = async () => {
    const response = await downloadImportSample('order')
    const url = URL.createObjectURL(
      new Blob([response.data], { type: 'text/csv' }),
    )
    const link = document.createElement('a')
    link.href = url
    link.download = 'order_sample.csv'
    link.click()
    URL.revokeObjectURL(url)
  }

  const toggleFilterForm = () => {
    setShowFilter(!showFilter)
  }

  const handleExport = async () => {
    const filters = getQuery(FILTER_FIELD_ORDERS)

    if (!filters) {
      return toast.error('Filters are required to export data!', {
        duration: 3000,
      })
    }

    const masterFilter = filterEmptyValues(filters)

    if (Object.keys(masterFilter).length <= 0) {
      return toast.error('Please select at least on filter to export data!', {
        duration: 3000,
      })
    }

    const loadingToast = toast.loading('Loading...', { duration: Infinity })

    try {
      await downloadOrders({ filters: masterFilter })
      toast.success('File will be sent to you in email!', {
        id: loadingToast,
        duration: 3000,
      })
    } catch (error) {
      handleError(error, loadingToast)
    }
  }

  const triggerCron = async (jobTitle: string) => {
    const loadingToast = toast.loading(`Initializing ${jobTitle} Job`, {
      duration: Infinity,
    })

    setTriggerLoading(jobTitle)

    try {
      if (jobTitle === CRON_TYPE.ORDER) {
        await triggerOrderSync()
      } else if (jobTitle === CRON_TYPE.RECONCILIATION) {
        await triggerReconciliationSync()
      } else if (jobTitle === CRON_TYPE.RMA) {
        await triggerRmaSync()
      } else if (jobTitle === CRON_TYPE.LASTMILE) {
        await triggerLastmileSync()
      }

      toast.success(`${jobTitle} Job Initiated`, {
        id: loadingToast,
        duration: 3000,
      })
    } catch (error) {
      handleError(error, loadingToast)
    } finally {
      setTriggerLoading(undefined)
    }
  }

  return (
    <>
      <Helmet>
        <title>GAG Aggregator | Order Management</title>
      </Helmet>

      {/* Manual Sync */}
      {isEditor && (
        <>
          <h3 className="title">Manual Sync</h3>
          <div className="mb-6 flex items-center gap-4">
            <Button
              type="button"
              variant="outline"
              buttonText="Resync Orders"
              loading={triggerLoading === CRON_TYPE.ORDER}
              onClick={() => triggerCron(CRON_TYPE.ORDER)}
            />
            <Button
              type="button"
              variant="outline"
              buttonText="Resync RMA"
              loading={triggerLoading === CRON_TYPE.RMA}
              onClick={() => triggerCron(CRON_TYPE.RMA)}
            />
            <Button
              type="button"
              variant="outline"
              buttonText="Resync Reconciliation"
              loading={triggerLoading === CRON_TYPE.RECONCILIATION}
              onClick={() => triggerCron(CRON_TYPE.RECONCILIATION)}
            />
            <Button
              type="button"
              variant="outline"
              buttonText="Resync to Lastmile"
              loading={triggerLoading === CRON_TYPE.LASTMILE}
              onClick={() => triggerCron(CRON_TYPE.LASTMILE)}
            />
          </div>
        </>
      )}

      {/* Orders Table Section */}
      <div
        className={`mb-2 flex flex-wrap items-center justify-between pt-4 md:mb-0 md:flex-nowrap`}
      >
        <h3 className="title">Current Orders</h3>
        <Button
          type="button"
          buttonText="Reports"
          variant="custom"
          iconClassName="!h-5 !w-5 text-dataTableIcon"
          icon={<ReportIcon />}
          className="rounded-lg px-3.5 py-2 text-sm font-semibold text-sidebar-primary hover:bg-gray-200"
          onClick={() => setShowReportsModal(true)}
        />
      </div>

      <OrderReportsModal
        isOpen={showReportsModal}
        closeModal={() => setShowReportsModal(false)}
      />

      <DataTableController
        isEditor={isEditor}
        columns={columns}
        handleFilter={toggleFilterForm}
        showFilter={showFilter}
        handleImport={importOrdersApi}
        handleExport={handleExport}
        activeColumnKey="order"
        placeholder="Search by Order ID, Invoice No and Short Code"
        defaultSelectedColumns={selectedColumns}
        loading={loading}
        importColumns={importColumns}
        importModuleName="order"
        onDownloadSample={handleDownloadSample}
      />

      {!showFilter && <ActiveFilterChip filterKey={FILTER_FIELD_ORDERS} />}

      {showFilter ? (
        <OrderFilterForm toggleFilterForm={toggleFilterForm} />
      ) : (
        <OrdersTable
          setSelectedColumns={setSelectedColumns}
          loading={loading}
          setLoading={setLoading}
        />
      )}
    </>
  )
}

export default OrderListPage
